"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.useOffsetCursorParallelPagination = void 0;
const react_1 = require("react");
const react_2 = require("@apollo/client/react");
const pagination_utils_1 = require("./pagination.utils");
const inFlightRequests = new Map();
const getRemainingPageOffsets = (totalCount, pageSize, includeInitialPage) => {
    const totalPages = Math.ceil(totalCount / pageSize);
    return Array.from({ length: Math.max(totalPages - (includeInitialPage ? 0 : 1), 0) }, (_, index) => (index + (includeInitialPage ? 0 : 1)) * pageSize);
};
const useOffsetCursorParallelPagination = ({ query, initialData, baseVariables, pageSize, totalCount, hasNextPage, mergeResults, includeInitialPage = false, skip = false, requestKey, fetchPolicy = 'no-cache', }) => {
    const client = (0, react_2.useApolloClient)();
    const requestId = (0, react_1.useRef)(0);
    const inFlightRequestKey = (0, react_1.useRef)();
    const loadedRequestKey = (0, react_1.useRef)();
    const [data, setData] = (0, react_1.useState)();
    const [isLoading, setIsLoading] = (0, react_1.useState)(false);
    const [error, setError] = (0, react_1.useState)();
    const resolvedRequestKey = (0, react_1.useMemo)(() => requestKey !== null && requestKey !== void 0 ? requestKey : JSON.stringify({ baseVariables, pageSize, totalCount }), [baseVariables, pageSize, requestKey, totalCount]);
    const hasPendingRequest = !skip
        && (initialData !== undefined || includeInitialPage)
        && loadedRequestKey.current !== resolvedRequestKey
        && inFlightRequestKey.current !== resolvedRequestKey
        && (includeInitialPage || Boolean(hasNextPage));
    (0, react_1.useEffect)(() => {
        if (skip || (!initialData && !includeInitialPage)) {
            requestId.current += 1;
            inFlightRequestKey.current = undefined;
            loadedRequestKey.current = undefined;
            setData(undefined);
            setIsLoading(false);
            setError(undefined);
            return;
        }
        if (loadedRequestKey.current === resolvedRequestKey || inFlightRequestKey.current === resolvedRequestKey) {
            return;
        }
        requestId.current += 1;
        const currentRequestId = requestId.current;
        const finishCurrentRequest = () => {
            if (requestId.current !== currentRequestId)
                return;
            inFlightRequestKey.current = undefined;
            setIsLoading(false);
        };
        const handleCurrentRequestError = (error) => {
            if (requestId.current !== currentRequestId)
                return;
            setError(error.message);
            loadedRequestKey.current = resolvedRequestKey;
        };
        const inFlightRequest = inFlightRequests.get(resolvedRequestKey);
        if (inFlightRequest !== undefined) {
            inFlightRequestKey.current = resolvedRequestKey;
            setData(initialData);
            setError(undefined);
            setIsLoading(true);
            void inFlightRequest
                .then((data) => {
                if (requestId.current !== currentRequestId)
                    return;
                setData(data);
                loadedRequestKey.current = resolvedRequestKey;
            })
                .catch(handleCurrentRequestError)
                .finally(finishCurrentRequest);
            return;
        }
        const remainingPageOffsets = getRemainingPageOffsets(totalCount, pageSize, includeInitialPage);
        const remainingPageVariables = remainingPageOffsets.map((offset) => (Object.assign(Object.assign({}, baseVariables), { first: pageSize, after: offset === 0 ? null : (0, pagination_utils_1.createCustomOffsetCursor)(offset - 1) })));
        inFlightRequestKey.current = resolvedRequestKey;
        setData(initialData);
        setError(undefined);
        if ((!includeInitialPage && !hasNextPage) || !remainingPageVariables.length) {
            loadedRequestKey.current = resolvedRequestKey;
            inFlightRequestKey.current = undefined;
            setIsLoading(false);
            return;
        }
        setIsLoading(true);
        const fetchRemainingPages = Promise.all(remainingPageVariables.map((variables) => client.query({
            query,
            variables,
            fetchPolicy,
        })))
            .then((responses) => {
            const responseData = responses.map((response) => response.data);
            const [fetchedInitialData, ...fetchMoreResults] = responseData;
            const mergedData = includeInitialPage
                ? mergeResults(fetchedInitialData !== null && fetchedInitialData !== void 0 ? fetchedInitialData : initialData, fetchMoreResults)
                : mergeResults(initialData, responseData);
            return mergedData;
        })
            .finally(() => {
            inFlightRequests.delete(resolvedRequestKey);
        });
        inFlightRequests.set(resolvedRequestKey, fetchRemainingPages);
        void fetchRemainingPages
            .then((mergedData) => {
            if (requestId.current !== currentRequestId)
                return;
            setData(mergedData);
            loadedRequestKey.current = resolvedRequestKey;
        })
            .catch(handleCurrentRequestError)
            .finally(() => {
            finishCurrentRequest();
        });
    }, [
        baseVariables,
        client,
        fetchPolicy,
        hasNextPage,
        includeInitialPage,
        initialData,
        mergeResults,
        pageSize,
        query,
        resolvedRequestKey,
        skip,
        totalCount,
    ]);
    return {
        data,
        isLoading: isLoading || hasPendingRequest,
        error,
    };
};
exports.useOffsetCursorParallelPagination = useOffsetCursorParallelPagination;
